import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { StudyPlanData } from '../../types/study-plan';
import { generateSampleStudyPlan, SUBJECTS } from '../../utils/studyPlanMockData';
import WeeklyCalendarView from './WeeklyCalendarView';
import DailyTaskView from './DailyTaskView';

interface StudyPlanResultsProps {
  studyPlanData: StudyPlanData;
  onRestart: () => void;
}

const StudyPlanResults: React.FC<StudyPlanResultsProps> = ({ studyPlanData, onRestart }) => {
  const [viewMode, setViewMode] = useState<'weekly' | 'daily'>('weekly');
  
  // Generate mock study plan based on user preferences
  const studyTasks = generateSampleStudyPlan();

  const getExamTypeName = () => {
    switch (studyPlanData.examType) {
      case 'neet': return 'NEET';
      case 'jee-mains': return 'JEE Mains';
      case 'jee-advance': return 'JEE Advanced';
      default: return 'Exam';
    }
  };

  const getWeakSubjects = () => {
    return studyPlanData.subjectStrengths
      ?.filter(s => s.level === 'weak')
      .map(s => SUBJECTS.find(sub => sub.id === s.subjectId)?.name)
      .filter(Boolean) || [];
  };

  const getStudyHours = () => {
    const slots = studyPlanData.studyPattern?.selectedSlots || [];
    return slots.length * 2; // Assuming 2 hours per slot
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="study-plan-results"
    >
      <div className="container-fluid py-4">
        {/* Success Header */}
        <div className="text-center mb-5">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="mb-3"
          >
            <div className="success-icon mx-auto bg-success text-white rounded-circle d-flex align-items-center justify-content-center"
                 style={{ width: '100px', height: '100px' }}>
              <i className="fas fa-check fa-3x"></i>
            </div>
          </motion.div>
          
          <h1 className="display-4 fw-bold text-success mb-3">
            Study Plan Generated Successfully!
          </h1>
          <p className="lead text-muted">
            Your personalized {getExamTypeName()} preparation plan is ready
          </p>
        </div>

        {/* Plan Summary */}
        <div className="row g-4 mb-5">
          <div className="col-lg-3 col-md-6">
            <div className="card border-primary border-2 h-100">
              <div className="card-body text-center">
                <div className="text-primary mb-2">
                  <i className="fas fa-graduation-cap fa-2x"></i>
                </div>
                <h5 className="card-title text-primary">Target Exam</h5>
                <p className="card-text fw-bold">{getExamTypeName()}</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-3 col-md-6">
            <div className="card border-info border-2 h-100">
              <div className="card-body text-center">
                <div className="text-info mb-2">
                  <i className="fas fa-clock fa-2x"></i>
                </div>
                <h5 className="card-title text-info">Daily Study Time</h5>
                <p className="card-text fw-bold">{getStudyHours()} Hours</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-3 col-md-6">
            <div className="card border-warning border-2 h-100">
              <div className="card-body text-center">
                <div className="text-warning mb-2">
                  <i className="fas fa-exclamation-triangle fa-2x"></i>
                </div>
                <h5 className="card-title text-warning">Focus Areas</h5>
                <p className="card-text fw-bold">
                  {getWeakSubjects().length > 0 
                    ? getWeakSubjects().join(', ') 
                    : 'All Subjects'
                  }
                </p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-3 col-md-6">
            <div className="card border-success border-2 h-100">
              <div className="card-body text-center">
                <div className="text-success mb-2">
                  <i className="fas fa-calendar-alt fa-2x"></i>
                </div>
                <h5 className="card-title text-success">Study Days</h5>
                <p className="card-text fw-bold">7 Days/Week</p>
              </div>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="card mb-4">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 text-primary">
                <i className="fas fa-calendar-week me-2"></i>
                Your Study Schedule
              </h4>
              
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${viewMode === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('weekly')}
                >
                  <i className="fas fa-calendar-week me-2"></i>
                  Weekly View
                </button>
                <button
                  type="button"
                  className={`btn ${viewMode === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('daily')}
                >
                  <i className="fas fa-list me-2"></i>
                  Daily Tasks
                </button>
              </div>
            </div>
          </div>
          
          <div className="card-body">
            {viewMode === 'weekly' ? (
              <WeeklyCalendarView tasks={studyTasks} />
            ) : (
              <DailyTaskView tasks={studyTasks} />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="text-center">
          <button
            type="button"
            onClick={onRestart}
            className="btn btn-outline-primary btn-lg me-3"
          >
            <i className="fas fa-redo me-2"></i>
            Create New Plan
          </button>
          
          <button
            type="button"
            className="btn btn-success btn-lg"
            onClick={() => window.print()}
          >
            <i className="fas fa-download me-2"></i>
            Save Plan
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default StudyPlanResults;